import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileStack, Home, AlertTriangle, Wallet, Sparkles, CalendarClock, Activity, ArrowRight, GripVertical } from "lucide-react";
import api from "@/lib/api";
import StatusBadge, { STATUS_COLORS } from "@/components/StatusBadge";
import { useAppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { tr } from "@/lib/i18n";

const fmt = (n) => `$${(n || 0).toLocaleString("en-CA", { maximumFractionDigits: 0 })}`;

const shade = (hex, pct) => {
    const n = parseInt(hex.slice(1), 16);
    const f = (v) => Math.max(0, Math.min(255, Math.round(v * (1 + pct / 100))));
    return `rgb(${f(n >> 16)}, ${f((n >> 8) & 255)}, ${f(n & 255)})`;
};

function IsoCity({ byStatus }) {
    const data = (byStatus || []).slice(0, 6);
    const max = Math.max(...data.map((s) => s.value), 1);
    return (
        <div className="iso-scene w-full h-full min-h-[210px] flex items-center justify-center" data-testid="iso-city">
            <div className="iso-plane relative w-[230px] h-[160px]">
                <div className="iso-ground bg-white/[0.06] border border-white/10" style={{ boxShadow: "inset 0 0 40px rgba(0,180,216,0.15)" }} />
                {data.map((s, i) => {
                    const col = i % 3;
                    const row = Math.floor(i / 3);
                    const h = 24 + Math.round((s.value / max) * 78);
                    const c = STATUS_COLORS[s.name] || "#00B4D8";
                    return (
                        <div
                            key={s.name}
                            className="iso-cuboid"
                            style={{ left: 24 + col * 70, top: 20 + row * 64, width: 42, height: 42, "--h": `${h}px`, animationDelay: `${0.35 + i * 0.14}s` }}
                            title={`${s.name}: ${s.value} files`}
                        >
                            <div className="iso-face iso-top" style={{ backgroundColor: shade(c, 12) }} />
                            <div className="iso-face iso-front" style={{ backgroundColor: shade(c, -22) }} />
                            <div className="iso-face iso-right" style={{ backgroundColor: shade(c, -40) }} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TriageQueue() {
    const { tr } = useLanguage();
    const [queue, setQueue] = useState([]);
    useEffect(() => { api.get("/dashboard/triage").then((r) => setQueue(r.data)).catch(() => {}); }, []);
    if (!queue.length) return null;
    const LEVEL_STYLE = { critical: "bg-[#EF476F]/15 text-[#EF476F]", urgent: "bg-[#FB8500]/15 text-[#C25E00]", watch: "bg-[#FFB703]/15 text-[#B8860B]" };
    return (
        <div className="bg-card rounded-[18px] border border-[#EF476F]/20 dark:border-white/10 p-5 card-shadow" data-testid="triage-queue">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-navy dark:text-white flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#EF476F]" /> {tr("Problem file triage queue")}</h3>
                <span className="text-[11px] font-bold text-slate-400">{queue.length} {tr("flagged")}</span>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                {queue.map((a) => (
                    <Link key={a.id} to={`/applications/${a.id}`} data-testid={`triage-item-${a.file_number}`}
                        className="p-3.5 rounded-[12px] bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 hover:border-[#EF476F]/40 transition-colors block">
                        <div className="flex items-center justify-between gap-2">
                            <span className="font-data text-[11px] font-bold text-[#0092B0]">{a.file_number}</span>
                            <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${LEVEL_STYLE[a.triage?.level] || ""}`}>{tr({ watch: "Watch", urgent: "Urgent", critical: "Critical" }[a.triage?.level] || a.triage?.level)}</span>
                        </div>
                        <div className="text-sm font-semibold text-navy dark:text-white mt-1 truncate">{a.title}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 truncate">{a.triage?.reason || tr("No reason recorded")}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{a.planner_name} · {a.elapsed_days}d {tr("in system")}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { openAI } = useAppShell();
    const { user } = useAuth();
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [kpiOrder, setKpiOrder] = useState(() => {
        try { return JSON.parse(localStorage.getItem("apptrack_kpi_order") || "null"); } catch { return null; }
    });
    const [dragKpi, setDragKpi] = useState(null);

    useEffect(() => {
        api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data)
        return (
            <div className="space-y-4" data-testid="dashboard-loading">
                <div className="h-56 rounded-[24px] bg-white/60 dark:bg-white/5 animate-pulse" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-[18px] bg-white/60 dark:bg-white/5 animate-pulse" />)}</div>
            </div>
        );

    const hour = new Date().getHours();
    const isManager = data.role_view === "manager";
    const kpis = [
        { label: t("dash.kpi_active"), value: data.kpis.active_files, icon: FileStack, testid: "kpi-active-files" },
        { label: t("dash.kpi_units"), value: data.kpis.total_units.toLocaleString(), icon: Home, testid: "kpi-units" },
        { label: t("dash.kpi_overdue"), value: data.kpis.overdue_circulations, icon: AlertTriangle, warn: data.kpis.overdue_circulations > 0, testid: "kpi-overdue" },
        isManager
            ? { label: t("dash.kpi_revenue"), value: fmt(data.kpis.revenue_ytd), icon: Wallet, testid: "kpi-revenue" }
            : { label: t("dash.kpi_myfiles"), value: (data.my_files || []).filter((f) => !["Approved", "Rejected"].includes(f.status)).length, icon: FileStack, testid: "kpi-my-files" },
    ];

    return (
        <div data-testid="dashboard-page" className="space-y-5">
            {/* Hero with 3D isometric city */}
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="rounded-[24px] bg-navy text-white overflow-hidden relative shadow-[0_30px_70px_-30px_rgba(10,37,64,0.6)]"
                data-testid="dashboard-hero">
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: "radial-gradient(ellipse 55% 70% at 80% 15%, #00B4D8 0%, transparent 60%), radial-gradient(ellipse 40% 50% at 5% 100%, #2DD4BF 0%, transparent 55%)" }} />
                <div className="relative grid lg:grid-cols-2 gap-6 p-8 sm:p-10 items-center">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#7CD4EA]">{tr("YOUR CITY, LIVE")}</div>
                        <h1 className="mt-2 font-heading font-bold text-3xl sm:text-4xl tracking-tight">
                            {t("greeting." + (hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening"))}, {user?.name?.split(" ")[0]}
                        </h1>
                        <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-md">
                            {tr("Every building on this skyline is a live application status.")} {data.kpis.active_files} {tr("files are active across Brampton right now.")}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button data-testid="dashboard-ai-btn"
                                onClick={() => openAI({ type: "data" }, "Give me a snapshot of development activity: applications by status and type, total proposed units, and revenue collected vs outstanding.")}
                                className="brand-btn inline-flex items-center gap-2 px-5 py-3 rounded-[12px] text-sm font-bold">
                                <Sparkles className="w-4 h-4" /> {t("dash.briefing")}
                            </button>
                            <Link to="/applications" data-testid="hero-view-apps" className="inline-flex items-center gap-2 px-5 py-3 rounded-[12px] text-sm font-bold bg-white/10 border border-white/15 hover:bg-white/20 transition-colors">
                                {tr("Open pipeline")} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2">
                            {data.by_status.map((s) => (
                                <span key={s.name} className="inline-flex items-center gap-1.5 text-[11px] text-slate-300">
                                    <span className="w-2 h-2 rounded-[3px]" style={{ backgroundColor: STATUS_COLORS[s.name] }} />
                                    {tr(s.name)} · <span className="font-data font-semibold text-white">{s.value}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                    <IsoCity byStatus={data.by_status} />
                </div>
            </motion.div>

            {/* Problem file triage queue */}
            <TriageQueue />

            {/* KPI pills — drag to reorder */}
            <div className="flex items-center gap-2 text-[11px] text-slate-400 -mb-1">
                <GripVertical className="w-3.5 h-3.5" /> {tr("Drag cards to rearrange your dashboard")}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
                {(kpiOrder
                    ? [...kpiOrder.map((id) => kpis.find((k) => k.testid === id)).filter(Boolean), ...kpis.filter((k) => !kpiOrder.includes(k.testid))]
                    : kpis
                ).map((k, i) => (
                    <div key={k.label} data-testid={k.testid}
                        draggable
                        onDragStart={() => setDragKpi(k.testid)}
                        onDragEnd={() => setDragKpi(null)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            if (!dragKpi || dragKpi === k.testid) return;
                            const ids = (kpiOrder || kpis.map((x) => x.testid)).filter((id) => id !== dragKpi);
                            ids.splice(ids.indexOf(k.testid), 0, dragKpi);
                            setKpiOrder(ids);
                            localStorage.setItem("apptrack_kpi_order", JSON.stringify(ids));
                            setDragKpi(null);
                        }}
                        className={`bg-card rounded-[18px] border border-white/70 dark:border-white/10 p-5 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] hover:shadow-[0_20px_44px_-16px_rgba(10,37,64,0.3)] hover:-translate-y-0.5 transition-all cursor-grab active:cursor-grabbing ${dragKpi === k.testid ? "opacity-40 scale-95" : ""}`}>
                        <div className="flex items-center justify-between">
                            <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-400 flex items-center gap-1.5">
                                <GripVertical className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />{tr(k.label)}
                            </div>
                            <div className={`w-8 h-8 rounded-[10px] flex items-center justify-center ${k.warn ? "bg-[#EF476F]/10" : "bg-[#00B4D8]/10"}`}>
                                <k.icon className={`w-4 h-4 ${k.warn ? "text-[#EF476F]" : "text-teal"}`} />
                            </div>
                        </div>
                        <div className={`mt-2 font-data font-bold text-[26px] ${k.warn ? "text-[#EF476F]" : "text-navy dark:text-white"}`}>{k.value}</div>
                    </div>
                ))}
            </div>

            {/* Role-based view: planner sees own files, manager sees team workload */}
            {isManager ? (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="bg-card rounded-[20px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] overflow-hidden" data-testid="manager-workload">
                    <div className="px-6 py-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                        <div>
                            <h2 className="font-heading font-bold text-navy dark:text-white">{t("dash.team_workload")}</h2>
                            <p className="text-xs text-slate-400 mt-0.5">{t("dash.team_sub")}</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 border border-slate-200 dark:border-white/10 rounded-full px-2.5 py-1">{t("dash.manager")}</span>
                    </div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100 dark:border-white/5">
                                <th className="px-6 py-3 font-bold">{tr("Planner")}</th>
                                <th className="px-6 py-3 font-bold text-right">{tr("Active files")}</th>
                                <th className="px-6 py-3 font-bold text-right">{tr("Total files")}</th>
                                <th className="px-6 py-3 font-bold text-right">{tr("Overdue circulations")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                            {(data.workload || []).map((w) => (
                                <tr key={w.planner} data-testid={`workload-row-${w.planner.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="hover:bg-slate-50/80 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-6 py-3.5">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-full bg-navy dark:bg-teal flex items-center justify-center text-white text-[10px] font-bold">{w.planner.split(" ").map((p) => p[0]).join("").slice(0, 2)}</div>
                                            <span className="font-semibold text-navy dark:text-slate-100">{w.planner}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-3.5 text-right font-data font-bold">{w.active}</td>
                                    <td className="px-6 py-3.5 text-right font-data text-slate-500">{w.total}</td>
                                    <td className="px-6 py-3.5 text-right">
                                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${w.overdue > 0 ? "bg-[#EF476F]/10 text-[#EF476F]" : "bg-[#06D6A0]/10 text-[#05A37E]"}`}>{w.overdue}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </motion.div>
            ) : (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="bg-card rounded-[20px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] overflow-hidden" data-testid="planner-my-files">
                    <div className="px-6 py-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                        <div>
                            <h2 className="font-heading font-bold text-navy dark:text-white">{t("dash.my_files")}</h2>
                            <p className="text-xs text-slate-400 mt-0.5">{t("dash.my_files_sub")}</p>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 border border-slate-200 dark:border-white/10 rounded-full px-2.5 py-1">{t("dash.planner")}</span>
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-white/5">
                        {(data.my_files || []).map((f) => (
                            <Link key={f.id} to={`/applications/${f.id}`} data-testid={`my-file-${f.file_number}`}
                                className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50/80 dark:hover:bg-white/5 transition-colors">
                                <div className="min-w-0 flex items-center gap-3">
                                    <span className="w-1.5 h-9 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[f.status] || "#94A3B8" }} />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-data text-xs text-teal font-semibold">{f.file_number}</span>
                                            <span className="text-[11px] text-slate-400">next: {f.next_milestone}{f.next_date ? ` · ${new Date(f.next_date).toLocaleDateString(document.documentElement.lang === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric" })}` : ""}</span>
                                        </div>
                                        <div className="text-sm font-semibold text-navy dark:text-slate-100 truncate mt-0.5">{f.title}</div>
                                    </div>
                                </div>
                                <StatusBadge status={f.status} />
                            </Link>
                        ))}
                        {(data.my_files || []).length === 0 && <div className="px-6 py-8 text-sm text-slate-400 text-center">{tr("No files assigned to you yet.")}</div>}
                    </div>
                </motion.div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                    className="lg:col-span-7 bg-card rounded-[20px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] overflow-hidden">
                    <div className="px-6 py-5 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                        <h2 className="font-heading font-bold text-navy dark:text-white">{t("dash.recent")}</h2>
                        <Link to="/applications" data-testid="view-all-apps-link" className="text-xs font-bold text-teal hover:underline inline-flex items-center gap-1">{t("dash.view_all")} <ArrowRight className="w-3 h-3" /></Link>
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-white/5">
                        {data.recent_applications.map((a) => (
                            <Link key={a.id} to={`/applications/${a.id}`} data-testid={`dash-app-${a.file_number}`}
                                className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50/80 dark:hover:bg-white/5 transition-colors">
                                <div className="min-w-0 flex items-center gap-3">
                                    <span className="w-1.5 h-9 rounded-full shrink-0" style={{ backgroundColor: STATUS_COLORS[a.status] || "#94A3B8" }} />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-data text-xs text-teal font-semibold">{a.file_number}</span>
                                            <span className="text-[11px] text-slate-400">{a.type}</span>
                                        </div>
                                        <div className="text-sm font-semibold text-navy dark:text-slate-100 truncate mt-0.5">{a.title}</div>
                                    </div>
                                </div>
                                <StatusBadge status={a.status} />
                            </Link>
                        ))}
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="lg:col-span-5 space-y-5">
                    <div className="bg-card rounded-[20px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-[10px] bg-[#FFB703]/12 flex items-center justify-center"><CalendarClock className="w-4 h-4 text-[#B8860B]" /></div>
                            <h2 className="font-heading font-bold text-navy dark:text-white">{t("dash.upcoming")}</h2>
                        </div>
                        <div className="space-y-3">
                            {data.upcoming_events.slice(0, 5).map((e, i) => (
                                <div key={i} className="flex items-center justify-between gap-3" data-testid={`upcoming-event-${i}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-navy dark:text-slate-100 truncate">{e.name}</div>
                                        <div className="text-[11px] text-slate-400 font-data">{e.file_number} · {e.title}</div>
                                    </div>
                                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${e.status === "current" ? "bg-[#FFB703]/15 text-[#B8860B]" : "bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-300"}`}>
                                        {new Date(e.date).toLocaleDateString(document.documentElement.lang === "fr" ? "fr-CA" : "en-CA", { month: "short", day: "numeric" })}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-card rounded-[20px] border border-white/70 dark:border-white/10 shadow-[0_10px_30px_-14px_rgba(10,37,64,0.18)] p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-[10px] bg-[#06D6A0]/12 flex items-center justify-center"><Activity className="w-4 h-4 text-[#05A37E]" /></div>
                            <h2 className="font-heading font-bold text-navy dark:text-white">{t("dash.activity")}</h2>
                        </div>
                        <div className="space-y-3.5">
                            {data.activities.slice(0, 6).map((a) => (
                                <div key={a.id} className="flex gap-3 text-sm" data-testid={`activity-${a.id.slice(0, 8)}`}>
                                    <div className="w-7 h-7 rounded-full bg-navy dark:bg-teal flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5">
                                        {a.actor.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                                    </div>
                                    <div className="min-w-0 leading-snug">
                                        <span className="font-semibold text-navy dark:text-slate-100">{a.actor}</span>{" "}
                                        <span className="text-slate-500 dark:text-slate-400">{a.action}</span>{" "}
                                        <span className="font-data text-xs text-teal">{a.target}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
